<template lang="pug">
span.root
	slot(
		name="default",
		:results="results",
		:query="query",
		:setQuery="setQuery",
		:sort="sort",
		:setSort="setSort",
		:resetSearch="resetSearch",
		:currentResultsStart="currentResultsStart",
		:currentResultsEnd="currentResultsEnd",
		:clearPreviousItemResults="clearPreviousItemResults",
		:executeSearch="executeSearch",
		:extend="extend",
		:extended="extended"
	)
		.search-query
			form.search-query__form(@submit.prevent.stop="() => false")
				.search-query__wrapper
					label.search-query__label(for="search-query__input") {{ config.queryPlaceholderText }}
					input#search-query__input.search-query__input(
						@keypress.enter.prevent="executeSearch()",
						v-model="queryModel",
						:placeholder="config.queryPlaceholderText",
						:autocomplete="autocomplete",
						type="text",
						name="search-query"
					)
					button.search-query__reset(
						v-if="config.showReset",
						type="button",
						@click="resetSearch()"
					)
						span {{ config.resetText }}
				button.search-query__submit(type="submit", @click="executeSearch()")
					span {{ config.querySubmitText }}

				.search-query__sort(v-if="config.showSort")
					label.search-query__sort-label(for="search-query__sort-select") Sort results
					select#search-query__sort-select(v-model="sortModel")
						option(v-for="option in results.sortOptions", :value="option") {{config.sortText}} {{ option.displayName }}

			small.search-query__results {{ label }}
</template>

<script>
import processTemplate from 'string-template';
export default {
	name: "BlueQuery",
	components: {},
	data() {
		return {};
	},
	computed: {
		autocomplete() {
			const autocomplete = this.config.queryAllowAutocomplete;
			return autocomplete === true ?
				'on' : 
				autocomplete === false ? 
					'off' : 
					typeof autocomplete === 'string' && autocomplete.trim().length ?
						autocomplete.trim() :
						false;
		},
		currentResultsStart() {
			let totalResults = this.results.totalResultCount;
			if (this.results.currentPage == 0 || this.config.preserveResults) {
				if (totalResults) return 1;
				return 0;
			}
			return this.results.currentPage * this.results.pageSize + 1;
		},
		currentResultsEnd() {
			let totalResults = this.results.totalResultCount;
			let currentPage = this.results.currentPage;
			let pageSize = this.results.pageSize;
			let max = (currentPage + 1) * pageSize;

			if (currentPage == 0) {
				if (totalResults <= pageSize) {
					return totalResults;
				}
				return pageSize;
			} else {
				if (max > totalResults) return totalResults;
				return max;
			}
		},
		label() {
			return processTemplate(this.config.resultsLabelTemplate, {
				start: this.currentResultsStart,
				end: this.currentResultsEnd,
				total: this.results.totalResultCount,
			});
		},
		results() {
			return this.$store.getters.results;
		},
		sort() {
			return this.$store.getters.sort;
		},
		sortModel: {
			get() {
				return this.sort;
			},
			set(val) {
				this.setSort(val);
			},
		},
		query() {
			return this.$store.getters.query;
		},
		queryModel: {
			get() {
				return this.query;
			},
			set(val) {
				this.setQuery(val);
			},
		},
		extended() {
			return this.$store.getters.extended;
		},
	},
	methods: {
		clearPreviousItemResults() {
			this.$store.commit("clearPreviousItemResults");
			this.$store.commit("setPageIndex", 0);
		},
		setSort(val) {
			let value;
			if (typeof val === "object" && val.hasOwnProperty("target"))
				value = val.target.value;
			else value = val;
			this.$store.dispatch("changeSort", value);
		},
		setQuery(val) {
			let value;
			if (typeof val === "object") value = val.target.value;
			else value = val;
			this.$store.commit("setQuery", value);
		},
		resetSearch(callback) {
			this.setQuery("");
			this.$store.dispatch("executeSearch", callback);
		},
		extend(callback) {
			this.$store.commit("extend", callback);
		},
		executeSearch(callback) {
			if (this.config.preserveResults) this.$store.commit("clearPreviousItemResults");
			return this.$store.dispatch("executeSearch", callback);
		},
	},
	created() { },
	mounted() { },
};
</script>

<style lang="scss" scoped>
span.root {
	display: contents;
}

.search-query {
	display: flex;
	flex-direction: column;
	padding: 0;
	margin: 0;
	flex: 1 1 100%;
	gap: 0.5rem;

	@include mq(sm) {
		gap: 0;
	}

	&__form {
		display: flex;
		flex-direction: column;
		position: relative;
		flex: 1 1 100%;
		padding: 0;
		margin: 0;
		gap: 0.5rem;

		@include mq(sm) {
			gap: 0;
			flex-direction: row;
		}
	}

	&__wrapper {
		display: flex;
		width: 100%;
		border: 1px solid var(--border-color);

		&:focus-within {
			box-shadow: inset 0 0 0 0.125rem var(--focus-color);
		}

		@include mq(sm) {
			flex: 1 1 100%;
		}
	}

	&__label,
	&__sort-label {
		@include hideVisually;
	}

	&__input {
		@include clean(input);
		width: 100%;
		margin: 0;
		padding: 1rem;

		@include mq(sm) {
			flex: 1 1 100%;
		}

		&:focus {
			box-shadow: none !important;
		}

		&::placeholder {
			color: var(--color);
			font-weight: 300;
		}
	}

	&__reset {
		align-self: center;
		flex: 0 0 auto;
		appearance: none;
		border: none;
		background: none;
		border-radius: 0;
		cursor: pointer;
		padding: 0.5rem;
		margin: 0 0.5rem;
		font-weight: 700;
		color: var(--reset-color);
		transition: var(--reset-transition);

		&:hover {
			color: var(--reset-hover-color);
		}
	}

	&__submit {
		@include clean(button);
		flex: 0 0 auto;
		padding: 1rem;
		background: var(--button-background);
		color: var(--button-color);
		transition: var(--button-transition);

		@include mqdn(sm) {
			order: 4;
		}

		&:hover {
			background: var(--button-hover-background);
			color: var(--button-hover-color);
		}
	}

	&__sort {
		position: relative;
		flex: 0 0 auto;
		margin: 0;
		border: 1px solid var(--border-color);
		display: flex;
		align-items: center;

		@include mq(sm) {
			margin-left: 1rem;
		}

		&::after {
			content: "";
			position: absolute;
			top: 50%;
			right: 1.25rem;
			height: 0.5rem;
			width: 0.5rem;
			border-left: 2px solid;
			border-bottom: 2px solid;
			transform: translate(50%, -75%) rotate(-45deg);
		}

		select {
			@include clean(input);
			padding: 1rem;
			padding-right: 2.5rem;
			min-width: 100%;
		}
	}

	&__results {
		line-height: 1;
		padding: 0.25rem 0.5rem;
		font-size: 0.75rem;
		align-self: flex-start;
		background: var(--label-background);
		color: var(--label-color);
	}
}
</style>